import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Input, Modal, notification } from 'antd';
import { Link } from 'react-router-dom';
import APIClient from '../api/api';
import { Button, Callout, DataTable, StatusTag } from '../components/ui';
import type { Column } from '../components/ui/DataTable';
import { PageHead } from '../layout';
import {
  blockLabels,
  formatCents,
  formatCountdown,
  formatMultiple,
  formatRate,
  headroomCents,
  PartnerCandidateRow,
  PartnerDashboard,
  stateLabels,
  stateTone,
} from './PartnerAuction.model';

/** A labelled figure in the header strip. */
function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className={`text-lg font-semibold tabular-nums ${tone ?? 'text-slate-900'}`}>{value}</span>
    </div>
  );
}

export default function PartnerAuction() {
  const [data, setData] = useState<PartnerDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [goLiveOpen, setGoLiveOpen] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await APIClient.getPartnerAuctionDashboard());
    } catch (e: any) {
      notification.error({ message: 'Could not load the experiment', description: e?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const header = data?.header;
  const report = data?.report ?? undefined;
  const candidates = useMemo(() => data?.candidates ?? [], [data]);

  const setControls = async (body: { killSwitchActive?: boolean; dryRun?: boolean; reason: string }) => {
    setBusy(true);
    try {
      await APIClient.updatePartnerAuctionControls(body);
      await load();
      notification.success({ message: 'Controls updated' });
      setGoLiveOpen(false);
      setReason('');
    } catch (e: any) {
      const blockers: string[] | undefined = e?.response?.data?.blockers;
      notification.error({
        message: e?.response?.data?.error ?? 'Could not update the controls',
        description: blockers?.length ? blockers.join('; ') : undefined,
        duration: 8,
      });
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<PartnerCandidateRow>[] = [
    {
      key: 'domain',
      title: 'Domain',
      render: (r) => (
        <div className="flex flex-col">
          <span className="font-medium">{r.domain}</span>
        </div>
      ),
    },
    {
      key: 'state',
      title: 'State',
      render: (r) => <StatusTag tone={stateTone(r.state) as any}>{stateLabels[r.state] ?? r.state}</StatusTag>,
    },
    {
      key: 'opening',
      title: <span title="Snapshot opening price, the revenue signal">Opening</span>,
      align: 'right',
      render: (r) => <span className="tabular-nums">{formatCents(r.snapshotStartPriceCents)}</span>,
    },
    {
      key: 'implied',
      title: <span title="Opening price divided by the listing multiple">Implied / mo</span>,
      align: 'right',
      render: (r) => <span className="tabular-nums">{formatCents(r.impliedMonthlyCents)}</span>,
    },
    {
      key: 'renewal',
      title: 'Renewal',
      align: 'right',
      render: (r) => (
        <span className="tabular-nums" title={`source: ${r.renewalSource}`}>
          {formatCents(r.snapshotRenewPriceCents)}
          {r.liveRenewPriceCents !== null && r.liveRenewPriceCents !== r.snapshotRenewPriceCents && (
            <span className="ml-1 text-[11px] text-amber-600">live {formatCents(r.liveRenewPriceCents)}</span>
          )}
        </span>
      ),
    },
    {
      key: 'requiredBid',
      title: <span title="The marketplace minimum bid, one increment above the opening price">Required bid</span>,
      align: 'right',
      render: (r) => <span className="tabular-nums">{formatCents(r.requiredFirstBidCents)}</span>,
    },
    {
      key: 'cap',
      title: <span title="Immutable cap from the snapshot economics">Cap</span>,
      align: 'right',
      render: (r) => <span className="tabular-nums">{formatCents(r.capCents)}</span>,
    },
    {
      key: 'headroom',
      title: <span title="Cap minus the required first bid. This is what decides viability.">Headroom</span>,
      align: 'right',
      render: (r) => {
        const h = headroomCents(r);
        if (h === null) return <span className="text-slate-400">—</span>;
        return (
          <span className={`tabular-nums ${h < 0 ? 'text-rose-600' : h < 100 ? 'text-amber-600' : 'text-emerald-700'}`}>
            {formatCents(h)}
          </span>
        );
      },
    },
    {
      key: 'working',
      title: <span title="Working maximum after live renewal and manual limits">Working max</span>,
      align: 'right',
      render: (r) => (
        <span className="tabular-nums">
          {formatCents(r.workingCapCents)}
          {r.manualMaxCents !== null && <span className="ml-1 text-[11px] text-sky-600">manual</span>}
        </span>
      ),
    },
    {
      key: 'live',
      title: 'Live bids',
      align: 'right',
      render: (r) => <span className="tabular-nums">{r.liveBidCount ?? '—'}</span>,
    },
    {
      key: 'ends',
      title: 'Ends in',
      align: 'right',
      render: (r) => <span className="tabular-nums">{formatCountdown(r.secondsToEnd)}</span>,
    },
    {
      key: 'blocks',
      title: 'Blocks',
      render: (r) =>
        r.blocks?.length ? (
          <div className="flex flex-wrap gap-1">
            {r.blocks.map((b) => (
              <StatusTag key={b} tone="warn">
                {blockLabels[b] ?? b}
              </StatusTag>
            ))}
          </div>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title="Partner Auctions"
        subtitle="Experimental source. Buys underpriced partner listings nobody else has bid on."
        actions={
          header && (
            <div className="flex items-center gap-2">
              <Link to="/tools/partner-auctions/settings" className="text-sm underline">
                Settings
              </Link>
              <Button
                onClick={() =>
                  setControls({
                    killSwitchActive: !header.killSwitchActive,
                    reason: header.killSwitchActive ? 'enabling source after review' : 'operator stop',
                  })
                }
                disabled={busy}
              >
                {header.killSwitchActive ? 'Release kill switch' : 'Activate kill switch'}
              </Button>
              {header.dryRun ? (
                <Button variant="primary" onClick={() => setGoLiveOpen(true)} disabled={busy}>
                  Go live
                </Button>
              ) : (
                <Button onClick={() => setControls({ dryRun: true, reason: 'returning to dry run' })} disabled={busy}>
                  Back to dry run
                </Button>
              )}
            </div>
          )
        }
      />

      {header && (
        <>
          {header.killSwitchActive && (
            <Callout tone="warn">
              <strong>Kill switch is active.</strong> No new bid calls are made, defence included. Discovery and
              measurement continue.
            </Callout>
          )}
          {header.dryRun && (
            <Callout tone="info">
              <strong>Dry run.</strong> The full decision path runs and records what it would have bid. Nothing is
              submitted to the marketplace.
            </Callout>
          )}
          {!header.liveReady && header.readinessBlockers?.length ? (
            <Callout tone="danger">
              <strong>Not ready to go live.</strong>
              <ul className="mt-1 list-disc pl-5">
                {header.readinessBlockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </Callout>
          ) : null}

          <div className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-5">
            <Stat label="Committed" value={formatCents(header.committedCents)} />
            <Stat label="Reserved" value={formatCents(header.reservedCents)} />
            <Stat label="Budget" value={formatCents(header.budgetCents)} />
            <Stat label="Wins" value={String(header.confirmedWins)} />
            <Stat
              label="Controls"
              value={header.killSwitchActive ? 'stopped' : header.dryRun ? 'dry run' : 'live'}
              tone={header.killSwitchActive ? 'text-rose-600' : header.dryRun ? 'text-amber-600' : 'text-emerald-700'}
            />
          </div>

          {report && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg border border-slate-200 bg-white p-4 text-sm md:grid-cols-3 lg:grid-cols-4">
              {[
                ['Candidates', String(report.eligibleCandidates)],
                ['Watched', String(report.watchedCandidates)],
                ['Unviable', formatRate(report.unviableRate)],
                ['Below floor', formatRate(report.belowRevenueFloorRate)],
                ['Competition skipped', formatRate(report.competitionSkippedRate)],
                ['Win rate', formatRate(report.winRate)],
                ['Would-bid events', String(report.wouldBidEvents)],
                ['Budget used', formatRate(report.budgetUtilization)],
                ['Aggregate multiple', formatMultiple(report.aggregateMultiple)],
              ].map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between gap-2">
                  <span className="text-slate-500">{label}</span>
                  <span className="tabular-nums font-medium">{value}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <DataTable
        columns={columns}
        data={candidates}
        rowKey={(r) => String(r.id)}
        loading={loading}
        emptyMessage="No candidates discovered yet. The feed is polled on an interval; most listings are filtered out."
      />

      <Modal
        open={goLiveOpen}
        title="Take the source live"
        okText="Go live"
        confirmLoading={busy}
        onCancel={() => setGoLiveOpen(false)}
        onOk={() => setControls({ dryRun: false, reason })}
        okButtonProps={{ disabled: reason.trim().length === 0 }}
      >
        <p className="mb-3 text-sm">
          This turns dry run off and starts the experiment clock, which is written once and cannot be moved
          afterwards. Real bids will be placed at the next snipe window.
        </p>
        {header?.killSwitchActive && (
          <Callout tone="warn">
            The kill switch is still active, so no bids will be placed until it is released as well.
          </Callout>
        )}
        <Form layout="vertical" className="mt-3">
          <Form.Item label="Reason (recorded in the audit trail)" required>
            <Input.TextArea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. shadow review complete, approved by ..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
